import type LogRocketType from 'logrocket';
import type { Platform } from '~/services/platform';

/**
 * LogRocket é carregado por `import()` DINÂMICO, e só DEPOIS do check de plataforma (2026-07-28).
 *
 * <p>POR QUE. `SUPPORTED_PLATFORMS` já exclui tizen/webos desde 2026-07-17 (Renato) — na TV o
 * `init()` retorna na primeira linha e o LogRocket NUNCA roda. Mas com `import` estático os
 * **71,7 KB** dele (medidos: buildei sem ele e o chunk caiu de 1458 pra 1386 KB) eram baixados e
 * parseados em todo boot da TV, pra nada.
 *
 * <p>Isso importa porque o preto da reciclagem periódica é dominado pelo boot: medido na QB50B,
 * `domInteractive` em 478ms e React montando só em 1134ms, com chunk único de 1,46 MB. Peso morto
 * no chunk principal é preto na tela da loja.
 *
 * <p>Também é o que o guardrail `cache-offline-first.md` pede: "se mantido, deve ser best-effort e
 * nunca bloquear/atrasar o boot ou o playback".
 *
 * <p>Os call sites seguem SÍNCRONOS e estáticos (`AnalyticsService`, `auth.store`, `useLogRocket`).
 * Foi a 1ª tentativa que falhou: por `import()` no hook, o Rollup avisou que o módulo também era
 * importado estaticamente por outros dois e não moveu nada. A carga sob demanda tem que ficar AQUI,
 * na única fronteira que todos atravessam.
 */

export interface LogRocketDevice {
  deviceId: string;
  playerId?: string | null;
  platform: Platform;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
}

// 2026-07-17 (Renato) — TVs (tizen/webos) REMOVIDAS. Gravar sessão 24/7 (DOM +
// console + network) numa TV de signage não compensa: CPU + banda contínua +
// privacidade. O replay do LogRocket foi feito p/ sessão de usuário interativa, não
// p/ kiosk que só toca vídeo. Debug de frota fica no heartbeat/RemoteOps/otaLog.
const SUPPORTED_PLATFORMS: Platform[] = ['browser', 'android', 'ios'];

class LogRocketService {
  /** Instância carregada sob demanda. `null` = ainda não carregou (ou plataforma não suportada). */
  private lr: typeof LogRocketType | null = null;
  private isInitialized = false;
  private appId = 'efflux/efflux-player';
  /**
   * Ligado só com `VITE_LOGROCKET=true` no build. **Default OFF desde 2026-10-15.**
   *
   * <p><b>Por que desligado.</b> Em produção a CSP do player bloqueia o script do LogRocket
   * (`cdn.logr-in.com/logger-1.min.js` × `script-src 'self'`), então ele inicializava, tentava
   * buscar o agente, apanhava e morria: <b>custo sem nenhum benefício</b> — banda, peso no
   * bundle e um erro vermelho fixo no console, sem gravar sessão alguma.</p>
   *
   * <p>O guardrail de cache/offline-first já pedia essa avaliação por outros motivos: replay
   * contínuo consome banda numa frota de 6000 telas, é inútil justamente quando a loja está
   * offline (o caso que mais importa proteger), grava a tela, e já causou crash em massa na
   * Tizen 6.5 (`Array.prototype.at` ausente no Chromium 85).</p>
   *
   * <p>Reversível: buildar com `VITE_LOGROCKET=true`. Se for religar, libere
   * `cdn.logr-in.com` na CSP antes — senão volta a ser custo puro.</p>
   */
  private isEnabled = import.meta.env.VITE_LOGROCKET === 'true';
  private currentPlatform: Platform = 'browser';
  private _isSupported = true;

  isSupported(): boolean {
    return (
      this._isSupported && SUPPORTED_PLATFORMS.includes(this.currentPlatform)
    );
  }

  init(platform: Platform): void {
    this.currentPlatform = platform;

    if (!SUPPORTED_PLATFORMS.includes(platform)) {
      console.log(
        `[LogRocket] Platform ${platform} not supported - skipping initialization`,
      );
      this._isSupported = false;
      return;
    }

    if (this.isInitialized || !this.isEnabled || !this.appId) return;

    // Fire-and-forget: a API pública continua síncrona (`void`). Os outros métodos já guardavam em
    // `isInitialized`, que só vira `true` no fim deste `then` — então nenhum deles pode ver `lr`
    // nulo. Chamadas que cheguem antes disso são descartadas, como já eram antes.
    void Promise.all([import('logrocket'), import('logrocket-react')])
      .then(([lrMod, reactMod]) => {
        const LogRocket = lrMod.default;
        const setupLogRocketReact = reactMod.default;
        this.lr = LogRocket;
        this.initWith(LogRocket, setupLogRocketReact, platform);
      })
      .catch((error) => {
        console.error('[LogRocket] Falha ao carregar o módulo:', error);
        this._isSupported = false;
      });
  }

  private initWith(
    LogRocket: typeof LogRocketType,
    setupLogRocketReact: (lr: typeof LogRocketType) => void,
    platform: Platform,
  ): void {
    try {
      LogRocket.init(this.appId, {
        release: import.meta.env.VITE_APP_VERSION || '1.0.0',
        console: { isEnabled: true, shouldAggregateConsoleErrors: true },
        network: {
          isEnabled: true,
          requestSanitizer: (request) => {
            for (const name of Object.keys(request.headers ?? {})) {
              if (
                ['authorization', 'x-api-key', 'x-device-token'].includes(
                  name.toLowerCase(),
                )
              ) {
                request.headers[name] = '[REDACTED]';
              }
              if (name.toLowerCase() === 'x-device-id')
                request.headers[name] = '[DEVICE]';
            }
            if (request.body) {
              try {
                const body = JSON.parse(request.body);
                if (body && typeof body === 'object' && 'deviceToken' in body) {
                  body.deviceToken = '[REDACTED]';
                  request.body = JSON.stringify(body);
                }
              } catch {
                /* non-JSON bodies carry no enrollment credential */
              }
            }
            // SSE token (short-lived) e device token também vazam pela query
            // string (?token=...) das URLs de SSE. Scrub o valor.
            if (request.url) {
              request.url = request.url.replace(
                /([?&]token=)[^&]+/gi,
                '$1[REDACTED]',
              );
            }
            return request;
          },
        },
        dom: { isEnabled: true, inputSanitizer: true },
      });

      setupLogRocketReact(LogRocket);
      this.isInitialized = true;
      console.log(`[LogRocket] Initialized for ${platform} platform`);
    } catch (error) {
      console.error('[LogRocket] Failed to initialize:', error);
      this._isSupported = false;
    }
  }

  identify(device: LogRocketDevice): void {
    if (!this.isSupported() || !this.isInitialized || !this.lr) return;
    this.lr.identify(device.deviceId, {
      playerId: device.playerId || 'pending',
      platform: device.platform,
      tenantId: device.tenantId,
      tenantSlug: device.tenantSlug,
      tenantName: device.tenantName,
    });
    console.log(
      '[LogRocket] Device identified:',
      device.deviceId.substring(0, 20) + '...',
    );
  }

  track(
    eventName: string,
    properties?: Record<string, string | number | boolean>,
  ): void {
    if (!this.isSupported() || !this.isInitialized || !this.lr) return;
    this.lr.track(eventName, properties);
  }

  trackError(error: Error | string, context?: Record<string, unknown>): void {
    if (!this.isSupported() || !this.isInitialized || !this.lr) return;
    this.lr.captureException(
      error instanceof Error ? error : new Error(String(error)),
      {
        extra: { ...context, platform: this.currentPlatform },
      },
    );
  }

  getSessionURL(): string | null {
    return this.isInitialized && this.lr ? this.lr.sessionURL : null;
  }
}

export const logRocketService = new LogRocketService();
